import { UseQueryResult } from '@tanstack/react-query';

import {
  CombinedUseQueryResultInput,
  CombinedUseQueryResultNoData,
} from '@hcs/types';

import { sum } from './math.utils';
import { areAllTruthy, isAnyTruthy } from './validators.utils';

export const combineReactQueryStatus = (
  statuses: UseQueryResult['status'][],
): UseQueryResult['status'] => {
  let isSuccess = true;
  for (const status of statuses) {
    if (status === 'error') {
      return 'error';
    } else if (status === 'pending') {
      return 'pending';
    }
    if (status !== 'success') {
      isSuccess = false;
    }
  }

  if (isSuccess) {
    return 'success';
  }
  return 'pending';
};

export const combineReactQueryFetchStatus = (
  statuses: UseQueryResult['fetchStatus'][],
): UseQueryResult['fetchStatus'] => {
  for (const status of statuses) {
    if (status === 'paused') {
      return 'paused';
    } else if (status === 'fetching') {
      return 'fetching';
    } else if (status === 'idle') {
      return 'idle';
    }
  }

  return 'idle';
};
interface CombineUseQueryResultInputs {
  isFetching: UseQueryResult['isFetching'][];
  isLoading: UseQueryResult['isLoading'][];
  isPending: UseQueryResult['isPending'][];
  isLoadingError: UseQueryResult['isLoadingError'][];
  isPaused: UseQueryResult['isPaused'][];
  isPlaceholderData: UseQueryResult['isPlaceholderData'][];
  isRefetching: UseQueryResult['isRefetching'][];
  isRefetchError: UseQueryResult['isRefetchError'][];
  isStale: UseQueryResult['isStale'][];
  isSuccess: UseQueryResult['isSuccess'][];
  status: UseQueryResult['status'][];
  fetchStatus: UseQueryResult['fetchStatus'][];
  dataUpdatedAt: UseQueryResult['dataUpdatedAt'][];
  errorUpdatedAt: UseQueryResult['errorUpdatedAt'][];
  errorUpdateCount: UseQueryResult['errorUpdateCount'][];
  failureCount: UseQueryResult['failureCount'][];
  isError: UseQueryResult['isError'][];
  isFetched: UseQueryResult['isFetched'][];
  isFetchedAfterMount: UseQueryResult['isFetchedAfterMount'][];
}

export const buildCombineQueryInputs = (
  queries: (CombinedUseQueryResultInput | null | undefined)[],
) => {
  const inputs: CombineUseQueryResultInputs = {
    isFetching: [],
    isLoading: [],
    isPending: [],
    isLoadingError: [],
    isPaused: [],
    isPlaceholderData: [],
    isRefetching: [],
    isRefetchError: [],
    isStale: [],
    isSuccess: [],
    isError: [],
    isFetched: [],
    isFetchedAfterMount: [],
    dataUpdatedAt: [],
    errorUpdatedAt: [],
    errorUpdateCount: [],
    failureCount: [],
    status: [],
    fetchStatus: [],
  };
  queries.forEach((query) => {
    if (!query) {
      return;
    }
    inputs.isFetching.push(query.isFetching);
    inputs.isLoading.push(query.isLoading);
    inputs.isPending.push(query.isPending);
    inputs.isLoadingError.push(query.isLoadingError);
    inputs.isPaused.push(query.isPaused);
    inputs.isPlaceholderData.push(query.isPlaceholderData);
    inputs.isRefetchError.push(query.isRefetchError);
    inputs.isStale.push(query.isStale);
    inputs.isSuccess.push(query.isSuccess);
    inputs.isError.push(query.isError);
    inputs.isFetched.push(query.isFetched);
    inputs.isFetchedAfterMount.push(query.isFetchedAfterMount);
    inputs.isRefetching.push(query.isRefetching);
    inputs.status.push(query.status);
    inputs.fetchStatus.push(query.fetchStatus);
    inputs.dataUpdatedAt.push(query.dataUpdatedAt);
    inputs.errorUpdateCount.push(query.errorUpdateCount);
    inputs.errorUpdatedAt.push(query.errorUpdatedAt);
    inputs.failureCount.push(query.failureCount);
  });
  return inputs;
};
export const combineUseQueryResult = (
  queries: (CombinedUseQueryResultInput | null | undefined)[],
): CombinedUseQueryResultNoData => {
  const inputs = buildCombineQueryInputs(queries);
  const d: CombinedUseQueryResultNoData = {
    isFetching: isAnyTruthy(inputs.isFetching),
    isLoading: isAnyTruthy(inputs.isLoading),
    isPending: isAnyTruthy(inputs.isPending),
    isLoadingError: isAnyTruthy(inputs.isLoadingError),
    isPaused: isAnyTruthy(inputs.isPaused),
    isPlaceholderData: isAnyTruthy(inputs.isPlaceholderData),
    isRefetching: isAnyTruthy(inputs.isRefetching),
    isRefetchError: isAnyTruthy(inputs.isRefetchError),
    isStale: isAnyTruthy(inputs.isStale),
    isSuccess: areAllTruthy(inputs.isSuccess),
    status: combineReactQueryStatus(inputs.status),
    fetchStatus: combineReactQueryFetchStatus(inputs.fetchStatus),
    isError: isAnyTruthy(inputs.isError),
    isFetched: areAllTruthy(inputs.isFetched),
    isFetchedAfterMount: areAllTruthy(inputs.isFetchedAfterMount),
    dataUpdatedAt: Math.max(...inputs.dataUpdatedAt),
    errorUpdatedAt: Math.max(...inputs.errorUpdatedAt),
    errorUpdateCount: sum(inputs.errorUpdateCount),
    failureCount: sum(inputs.failureCount),
  };
  return d;
};
